import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addDays, format, getISOWeek, nextMonday, previousMonday, startOfWeek, subDays } from 'date-fns';
import { enUS, hu } from 'date-fns/locale';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { finalize, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { StatCardComponent } from '../../../../../shared/statistics-cards/stat-card.component';
import { Building, ParkingData, ParkingLevel } from '../../models/parking-overview-model';
import { ParkingDataService } from '../../services/parking-overview.service';

/**
 * Extended level data with UI-specific properties
 */
interface UILevelData extends ParkingLevel {
  freeSpaces: number;
  occupancyRate: number;
}

/**
 * Extended building data with UI-specific properties
 */
interface UIBuilding extends Omit<Building, 'levels'> {
  levels: UILevelData[];
}

/**
 * Day data used for display in the heatmap
 */
interface DayData {
  date: string;
  displayDate: string;
  dayName: string;
  buildings: UIBuilding[];
}

/**
 * Level information for the heatmap rows
 */
interface LevelInfo {
  buildingId: string;
  buildingName: string;
  levelId: string;
  levelName: string;
  totalSpaces: number;
  key: string;
}

@Component({
  selector: 'app-parking-overview',
  templateUrl: './parking-overview.component.html',
  standalone: true,
  imports: [CommonModule, ButtonModule, CardModule, TableModule, TranslateModule, StatCardComponent],
  styles: [
    `
      .week-divider {
        position: relative;
      }
      .week-divider::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 4px;
        background-color: #4f46e5;
        z-index: 10;
      }
    `
  ]
})
export class ParkingOverviewComponent implements OnInit, OnDestroy {
  startDate: Date = startOfWeek(new Date(), { weekStartsOn: 1 }); // Start from Monday
  originalStartDate: Date = startOfWeek(new Date(), { weekStartsOn: 1 }); // Store the original start date
  buildingData: DayData[] = [];
  allLevels: LevelInfo[] = [];
  weekStart!: Date;
  weekEnd!: Date;
  totalSpaces: number = 0;
  currentOccupiedSpaces: number = 0;
  todayOccupiedSpaces: number = 0;
  todayTotalSpaces: number = 0;
  weeklyTotalSpaces: number = 0;
  weeklyAverageOccupied: number = 0;
  monthlyAverageOccupied: number = 0;
  viewedWeekPrimaryMonth: Date = new Date();
  loading: boolean = false;
  todayDataLoading: boolean = false;
  weeklyDataLoading: boolean = false;
  monthlyDataLoading: boolean = false;
  weekNumber: number = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private parkingService: ParkingDataService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    // Initialize loading states
    this.todayDataLoading = true;
    this.weeklyDataLoading = true;
    this.monthlyDataLoading = true;

    this.loadData();
    this.loadTodayData();
    this.loadMonthlyDataForViewedWeek();

    // Subscribe to language changes and reprocess data when language changes
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Reprocess the existing data with new locale
      if (this.buildingData.length > 0) {
        this.reprocessDataForLocale();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads and processes parking data for the current date range
   */
  loadData(): void {
    this.loading = true;
    this.weeklyDataLoading = true;
    this.monthlyDataLoading = true;

    // Generate array of 7 consecutive dates starting from startDate
    const weekDates = Array.from({ length: 7 }, (_, i) => addDays(this.startDate, i));

    // Set date ranges for the week
    this.weekStart = weekDates[0];
    this.weekEnd = weekDates[6];

    // Determine which month has the most days in this week
    this.viewedWeekPrimaryMonth = this.getPrimaryMonthForWeek(weekDates);

    // Start date and end date for API query
    const startDateStr = format(this.weekStart, 'yyyy-MM-dd');
    const endDateStr = format(this.weekEnd, 'yyyy-MM-dd');

    // Get week number for display
    this.weekNumber = getISOWeek(this.weekStart);

    // First load all parking spaces to get real capacity data
    this.parkingService
      .loadAllParkingSpaces()
      .pipe(
        finalize(() => {
          // After parking spaces are loaded, load the date range data
          this.parkingService
            .loadDataForDateRange(startDateStr, endDateStr)
            .pipe(
              finalize(() => {
                // Process the data after API call is complete (success or failure)
                this.processLoadedData(weekDates);
                this.loading = false;
                this.weeklyDataLoading = false;
                // Load monthly data for the primary month of viewed week
                this.loadMonthlyDataForViewedWeek();
              })
            )
            .subscribe();
        })
      )
      .subscribe();
  }

  /**
   * Process loaded data for the date range
   * @param weekDates Array of dates to process
   */
  private processLoadedData(weekDates: Date[]): void {
    // Map dates to day data with building and level information
    this.buildingData = weekDates
      .map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayData = this.parkingService.getDataForDate(dateStr);

        if (!dayData || !dayData.buildings || dayData.buildings.length === 0) {
          return {
            date: dateStr,
            displayDate: format(date, 'MMM dd', { locale: this.getDateLocale() }),
            dayName: format(date, 'EEE', { locale: this.getDateLocale() }),
            buildings: []
          };
        }

        return {
          date: dateStr,
          displayDate: format(date, 'MMM dd', { locale: this.getDateLocale() }),
          dayName: format(date, 'EEE', { locale: this.getDateLocale() }),
          buildings: dayData.buildings
            .filter((building) => building && building.levels && building.levels.length > 0)
            .map((building) => ({
              id: building.id,
              name: building.name,
              levels: building.levels
                .filter((level) => level && typeof level.totalSpaces === 'number')
                .map((level) => ({
                  id: level.id,
                  name: level.name,
                  totalSpaces: level.totalSpaces,
                  occupiedSpaces: level.occupiedSpaces,
                  reservedSpaces: level.reservedSpaces,
                  freeSpaces: level.totalSpaces - level.occupiedSpaces,
                  occupancyRate: level.totalSpaces > 0 ? Math.round((level.occupiedSpaces / level.totalSpaces) * 100) : 0
                }))
            }))
        };
      })
      .filter((day) => day.buildings && day.buildings.length > 0);

    if (this.buildingData.length > 0) {
      // Create a flat list of all levels from all buildings
      this.allLevels = this.buildingData[0].buildings.flatMap((building) =>
        building.levels.map((level) => ({
          buildingId: building.id,
          buildingName: building.name,
          levelId: level.id,
          levelName: level.name,
          totalSpaces: level.totalSpaces,
          key: `${building.id}-${level.id}`
        }))
      );

      // Calculate total spaces across all levels
      this.totalSpaces = this.allLevels.reduce((sum, level) => sum + level.totalSpaces, 0);

      // Calculate current occupied spaces (from the first day in the range)
      this.currentOccupiedSpaces = this.buildingData[0].buildings.reduce(
        (sum, building) => sum + building.levels.reduce((levelSum, level) => levelSum + level.occupiedSpaces, 0),
        0
      );

      // Calculate weekly statistics for the filtered time window
      this.calculateWeeklyStatistics();

      // Calculate monthly statistics (placeholder for now)
      this.calculateMonthlyStatistics();
    }
  }

  /**
   * Reprocess existing data with new locale formatting
   * Called when language changes to update date displays without reloading data
   */
  private reprocessDataForLocale(): void {
    // Update date formatting for existing buildingData
    this.buildingData = this.buildingData.map((dayData) => {
      const date = new Date(dayData.date);
      return {
        ...dayData,
        displayDate: format(date, 'MMM dd', { locale: this.getDateLocale() }),
        dayName: format(date, 'EEE', { locale: this.getDateLocale() })
      };
    });
  }

  /**
   * Get the appropriate date-fns locale based on current language
   * @returns The date-fns locale object
   */
  private getDateLocale() {
    const currentLang = this.translate.currentLang || this.translate.defaultLang || 'hu';
    return currentLang === 'en' ? enUS : hu;
  }

  /**
   * Check if a given date string is today
   * @param dateStr Date string in YYYY-MM-DD format
   * @returns True if the date is today
   */
  isToday(dateStr: string): boolean {
    const today = format(new Date(), 'yyyy-MM-dd');
    return dateStr === today;
  }

  /**
   * Get total occupied spaces for a specific day across all levels
   * @param dayData The day data to calculate totals for
   * @returns Total occupied spaces for the day
   */
  getDayTotalOccupied(dayData: DayData): number {
    if (!dayData.buildings || dayData.buildings.length === 0) {
      return 0;
    }
    return dayData.buildings.reduce((sum, building) => sum + building.levels.reduce((levelSum, level) => levelSum + level.occupiedSpaces, 0), 0);
  }

  /**
   * Get total spaces for a specific day across all levels
   * @param dayData The day data to calculate totals for
   * @returns Total spaces for the day
   */
  getDayTotalSpaces(dayData: DayData): number {
    if (!dayData.buildings || dayData.buildings.length === 0) {
      return 0;
    }
    return dayData.buildings.reduce((sum, building) => sum + building.levels.reduce((levelSum, level) => levelSum + level.totalSpaces, 0), 0);
  }

  /**
   * Get occupancy percentage for a specific day
   * @param dayData The day data to calculate percentage for
   * @returns Occupancy percentage as integer (no decimals)
   */
  getDayOccupancyPercentage(dayData: DayData): number {
    const totalSpaces = this.getDayTotalSpaces(dayData);
    const occupiedSpaces = this.getDayTotalOccupied(dayData);

    if (totalSpaces === 0) {
      return 0;
    }

    return Math.round((occupiedSpaces / totalSpaces) * 100);
  }

  /**
   * Calculate weekly utilization statistics for the filtered time window
   * This shows total parking spaces available and the average occupied spaces during the period
   */
  private calculateWeeklyStatistics(): void {
    if (!this.buildingData || this.buildingData.length === 0) {
      this.weeklyTotalSpaces = 0;
      this.weeklyAverageOccupied = 0;
      return;
    }

    // Calculate total available spaces (same across all days)
    this.weeklyTotalSpaces = this.totalSpaces;

    // Calculate average sold (occupied) spaces across the filtered time window
    let totalOccupiedAcrossAllDays = 0;
    let validDaysCount = 0;

    this.buildingData.forEach((dayData) => {
      if (dayData.buildings && dayData.buildings.length > 0) {
        const dayOccupied = dayData.buildings.reduce(
          (sum, building) => sum + building.levels.reduce((levelSum, level) => levelSum + level.occupiedSpaces, 0),
          0
        );
        totalOccupiedAcrossAllDays += dayOccupied;
        validDaysCount++;
      }
    });

    // Calculate average occupied spaces across the time window
    this.weeklyAverageOccupied = validDaysCount > 0 ? Math.round(totalOccupiedAcrossAllDays / validDaysCount) : 0;
  }

  /**
   * Get weekly utilization percentage for the statistics card
   * @returns Weekly utilization percentage as integer (no decimals) or loading indicator
   */
  getWeeklyOccupancyPercentage(): number | string {
    if (this.weeklyDataLoading) {
      return '...';
    }
    if (this.weeklyTotalSpaces === 0) {
      return 0;
    }
    return Math.round((this.weeklyAverageOccupied / this.weeklyTotalSpaces) * 100);
  }

  /**
   * Get today's occupied spaces display in "x / total" format (always shows today regardless of week paging)
   * @returns String in format "occupied / total" or loading indicator
   */
  getTodayOccupiedDisplay(): string {
    if (this.todayDataLoading) {
      return '...';
    }
    return `${this.todayOccupiedSpaces} / ${this.todayTotalSpaces}`;
  }

  /**
   * Get monthly utilization percentage for the statistics card
   * @returns Monthly utilization percentage as integer (no decimals)
   */
  getMonthlyOccupancyPercentage(): number {
    if (this.totalSpaces === 0) {
      return 0;
    }
    return Math.round((this.monthlyAverageOccupied / this.totalSpaces) * 100);
  }

  /**
   * Get monthly utilization display with percentage and month abbreviation for the viewed week's primary month
   * @returns String in format "XX% MMM" (e.g., "75% Aug") or loading indicator
   */
  getMonthlyUtilizationDisplay(): string {
    if (this.monthlyDataLoading) {
      return '...';
    }
    const percentage = this.getMonthlyOccupancyPercentage();
    const monthAbbr = format(this.viewedWeekPrimaryMonth, 'MMM', { locale: this.getDateLocale() });
    return `${percentage}% ${monthAbbr}`;
  }

  /**
   * Load today's parking data (independent of week paging)
   */
  private loadTodayData(): void {
    const today = format(new Date(), 'yyyy-MM-dd');
    this.todayDataLoading = true;

    // First ensure parking spaces are loaded
    this.parkingService
      .loadAllParkingSpaces()
      .pipe(
        finalize(() => {
          this.parkingService
            .loadDataForDateRange(today, today)
            .pipe(
              finalize(() => {
                this.calculateTodayStatistics();
                this.todayDataLoading = false;
              })
            )
            .subscribe({
              next: (success: boolean) => {
                if (success) {
                  console.log('Today parking data loaded successfully');
                }
              },
              error: (error: any) => {
                console.error('Error loading today parking data:', error);
              }
            });
        })
      )
      .subscribe();
  }

  /**
   * Load monthly parking data for the primary month of the viewed week
   */
  private loadMonthlyDataForViewedWeek(): void {
    this.monthlyDataLoading = true;

    this.parkingService
      .loadMonthlyDataForMonth(this.viewedWeekPrimaryMonth)
      .pipe(
        finalize(() => {
          // Calculate monthly statistics after data is loaded
          this.calculateMonthlyStatistics();
          this.monthlyDataLoading = false;
        })
      )
      .subscribe({
        next: (success: boolean) => {
          if (success) {
            console.log('Monthly parking data loaded successfully for viewed week month');
          }
        },
        error: (error: any) => {
          console.error('Error loading monthly parking data:', error);
          // Fallback to weekly data if monthly loading fails
          this.monthlyAverageOccupied = this.weeklyAverageOccupied;
        }
      });
  }

  /**
   * Calculate today's statistics (independent of week paging)
   */
  private calculateTodayStatistics(): void {
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayData = this.parkingService.getDataForDate(today);

    if (todayData && todayData.buildings && todayData.buildings.length > 0) {
      // Calculate today's occupied spaces
      this.todayOccupiedSpaces = todayData.buildings.reduce(
        (sum, building) => sum + building.levels.reduce((levelSum, level) => levelSum + level.occupiedSpaces, 0),
        0
      );

      // Calculate today's total spaces
      this.todayTotalSpaces = todayData.buildings.reduce(
        (sum, building) => sum + building.levels.reduce((levelSum, level) => levelSum + level.totalSpaces, 0),
        0
      );

      console.log(`Today statistics: ${this.todayOccupiedSpaces} / ${this.todayTotalSpaces}`);
    } else {
      // Don't fallback to potentially uninitialized data - keep loading state
      console.log('Today data not available yet, keeping loading state');
    }
  }

  /**
   * Calculate monthly utilization statistics using actual monthly data for the viewed week's primary month
   */
  private calculateMonthlyStatistics(): void {
    const monthlyData = this.parkingService.getMonthlyDataFromCacheForMonth(this.viewedWeekPrimaryMonth);

    if (monthlyData && monthlyData.length > 0) {
      // Use actual monthly data to calculate average
      this.monthlyAverageOccupied = this.parkingService.calculateAverageOccupancy(monthlyData);
      console.log(`Monthly statistics calculated from ${monthlyData.length} days: ${this.monthlyAverageOccupied} avg occupied`);
    } else {
      // Fallback to weekly data if no monthly data available
      this.monthlyAverageOccupied = this.weeklyAverageOccupied;
      console.log('Using weekly data as fallback for monthly statistics');
    }
  }

  /**
   * Determine which month has the most days in the given week
   * @param weekDates Array of 7 dates representing the week
   * @returns Date representing the primary month
   */
  private getPrimaryMonthForWeek(weekDates: Date[]): Date {
    const monthCounts = new Map<string, { count: number; date: Date }>();

    weekDates.forEach((date) => {
      const monthKey = format(date, 'yyyy-MM');
      if (monthCounts.has(monthKey)) {
        monthCounts.get(monthKey)!.count++;
      } else {
        monthCounts.set(monthKey, { count: 1, date: new Date(date) });
      }
    });

    // Find the month with the most days
    let primaryMonth = weekDates[0]; // Default to first day
    let maxCount = 0;

    monthCounts.forEach(({ count, date }) => {
      if (count > maxCount) {
        maxCount = count;
        primaryMonth = date;
      }
    });

    console.log(`Primary month for viewed week: ${format(primaryMonth, 'MMM yyyy')} (${maxCount} days)`);
    return primaryMonth;
  }

  /**
   * Determines the Tailwind CSS classes for the heatmap cell based on occupancy rate
   * @param freeSpaces Number of free spaces (kept for parameter compatibility)
   * @param totalSpaces Total number of spaces
   * @returns Tailwind CSS classes for the heatmap cell
   */
  getHeatmapClass(freeSpaces: number, totalSpaces: number): string {
    const occupiedSpaces = totalSpaces - freeSpaces;
    const occupancyRate = (occupiedSpaces / totalSpaces) * 100;

    // Colors are now reversed since we're showing occupied spaces
    if (occupiedSpaces === 0) return 'bg-green-500 text-white'; // Empty
    if (occupancyRate < 50) return 'bg-green-400 text-gray-800'; // Few occupancies
    if (occupancyRate < 90) return 'bg-amber-400 text-gray-800'; // Many occupancies
    return 'bg-red-500 text-white'; // Full
  }

  /**
   * Gets level data for a specific day and level
   * @param dayData The day data to search in
   * @param levelInfo The level info to match
   * @returns The UI level data if found, undefined otherwise
   */
  getLevelData(dayData: DayData, levelInfo: LevelInfo): UILevelData | undefined {
    const building = dayData.buildings.find((b) => b.id === levelInfo.buildingId);
    return building?.levels.find((l) => l.id === levelInfo.levelId);
  }

  /**
   * Navigate to the previous week
   */
  goToPreviousWeek(): void {
    // Move back one week (7 days)
    this.startDate = subDays(this.startDate, 7);
    this.loadData();
  }

  /**
   * Navigate to the next week
   */
  goToNextWeek(): void {
    // Move forward one week (7 days)
    this.startDate = addDays(this.startDate, 7);
    this.loadData();
  }

  /**
   * Check if we're currently at the original window
   * @returns True if current startDate matches the original start date
   */
  isAtOriginalWindow(): boolean {
    return format(this.startDate, 'yyyy-MM-dd') === format(this.originalStartDate, 'yyyy-MM-dd');
  }

  /**
   * Reset to the initial date range
   */
  goToToday(): void {
    // Reset to the original starting date
    this.startDate = new Date(this.originalStartDate);
    this.loadData();
  }

  /**
   * Format the full date range for display
   * @returns Formatted date range string
   */
  formatDateRange(): string {
    return `${format(this.weekStart, 'MMM dd', { locale: hu })} - ${format(this.weekEnd, 'MMM dd', { locale: hu })}`;
  }

  /**
   * Format the year for display
   * @returns Formatted year string
   */
  formatYear(): string {
    return format(this.weekStart, 'yyyy', { locale: hu });
  }
}
